import React, { useState, useEffect } from "react";
import { useTheme } from "@/contexts/ThemeContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useUser } from "@/contexts/UserContextProvider";
import { BeautifulTabsSelector } from "@/components/ui/BeautifulTabsSelector";
import { getUserInfo } from '@/AuthContext/userService';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from "@awesome.me/kit-269fbef2e2/icons/duotone/solid";
import { FnRunner } from "./FnRunner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { renderDate } from "../../../../../shared/renderDate";
import { HookRunner } from "./HookRunner";

export default function DiagnosticsSection() {
  const { user } = useUser();

  const [selectedTab, setSelectedTab] = useState<string>(
    "AuthContext"
  );
  const [exportedList, setExportedList] = useState<{ name: string; value: any; type: string; from: 'named' | 'default' }[]>([]);
  const [results, setResults] = useState<Record<string, { loading: boolean; result?: any; error?: any }>>({});
  const [debugExports, setDebugExports] = useState<any>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [hookDialog, setHookDialog] = useState<{ open: boolean; name: string; hookFn: Function | null }>({ open: false, name: '', hookFn: null });
  const [fnDialog, setFnDialog] = useState<{ open: boolean; name: string; fn: Function | null }>({ open: false, name: '', fn: null });

  // Static mapping from tab value to import function
  const moduleImportMap: Record<string, () => Promise<any>> = {
    // Contexts
    AuthContext: () => import('../../../../contexts/AuthContext'),
    TenancyContext: () => import('../../../../contexts/TenancyContext'),
    TenancyContextProvider: () => import('../../../../contexts/TenancyContextProvider'),
    ThemeContext: () => import('../../../../contexts/ThemeContext'),
    UserContextProvider: () => import('../../../../contexts/UserContextProvider'),
    UsersListProvider: () => import('../../../../contexts/UsersListProvider'),
    // Services
    assetsService: () => import('../../../../services/assetsService'),
    assuranceActionsService: () => import('../../../../services/assuranceActionsService'),
    authService: () => import('../../../../services/authService'),
    companyService: () => import('../../../../services/companyService'),
    complianceService: () => import('../../../../services/complianceService'),
    controlsService: () => import('../../../../services/controlsService'),
    dashboardDataService: () => import('../../../../services/dashboardDataService'),
    dataRetentionService: () => import('../../../../services/dataRetentionService'),
    documentationService: () => import('../../../../services/documentationService'),
    documentsService: () => import('../../../../services/documentsService'),
    ismsRolesService: () => import('../../../../services/ismsRolesService'),
    legalObligationsService: () => import('../../../../services/legalObligationsService'),
    logoutService: () => import('../../../../services/logoutService'),
    menusService: () => import('../../../../services/menusService'),
    frameworkService: () => import('../../../../services/frameworkService'),
    objectivesService: () => import('../../../../services/objectivesService'),
    policyService: () => import('../../../../services/policyService'),
    privacyRequestTracker: () => import('../../../../services/privacyRequestTracker'),
    readingProgressService: () => import('../../../../services/readingProgressService'),
    risksService: () => import('../../../../services/risksService'),
    rolesService: () => import('../../../../services/rolesService'),
    scopeService: () => import('../../../../services/scopeService'),
    soaService: () => import('../../../../services/soaService'),
    soaUpdatesService: () => import('../../../../services/soaUpdatesService'),
    specialInterestGroupsService: () => import('../../../../services/specialInterestGroupsService'),
    userReportsService: () => import('../../../../services/userReportsService'),
    userService: () => import('../../../../services/userService'),
    vendorScoreService: () => import('../../../../services/vendorScoreService'),
  };

  // Dynamically load and extract functions
  useEffect(() => {
    let cancelled = false;
    async function loadFunctions() {
      setExportedList([]);
      setDebugExports(null);
      const importFn = moduleImportMap[selectedTab];
      if (!importFn) {
        setDebugExports({ error: `No import mapping found for tab: ${selectedTab}` });
        return;
      }
      try {
        const mod = await importFn();
        // Debug: collect all exports and their types
        const debugInfo: any = {
          named: Object.entries(mod).map(([k, v]) => [k, typeof v]),
          default: mod.default && typeof mod.default === 'object'
            ? Object.entries(mod.default).map(([k, v]) => [k, typeof v])
            : undefined
        };
        setDebugExports(debugInfo);
        // Collect all exports (named)
        let exportsArr: { name: string; value: any; type: string; from: 'named' | 'default' }[] = [];
        Object.entries(mod).forEach(([key, value]) => {
          exportsArr.push({ name: key, value, type: typeof value, from: 'named' });
        });
        // Default export (object)
        if (mod.default && typeof mod.default === 'object') {
          Object.entries(mod.default).forEach(([key, value]) => {
            exportsArr.push({ name: key, value, type: typeof value, from: 'default' });
          });
        }
        if (!cancelled) setExportedList(exportsArr);
      } catch (err) {
        setDebugExports({ error: String(err) });
        if (!cancelled) setExportedList([]);
      }
    }
    loadFunctions();
    return () => { cancelled = true; };
  }, [selectedTab]);

  // Run a function and store result, or open dialog for hooks
  const handleRunFunction = (fnName: string, fn: Function) => {
    if (fnName.startsWith('use') && typeof fn === 'function') {
      setHookDialog({ open: true, name: fnName, hookFn: fn });
      return;
    }
    setFnDialog({ open: true, name: fnName, fn });
  };

  const TAB_GROUPS = [
    {
      group: "Contexts",
      options: [
        { value: "AuthContext", label: "Auth Context", description: "Debug AuthContext" },
        { value: "TenancyContext", label: "Tenancy Context", description: "Debug TenancyContext" },
        { value: "TenancyContextProvider", label: "Tenancy Context Provider", description: "Debug TenancyContextProvider" },
        { value: "ThemeContext", label: "Theme Context", description: "Debug ThemeContext" },
        { value: "UserContextProvider", label: "User Context Provider", description: "Debug UserContextProvider" },
        { value: "UsersListProvider", label: "Users List Provider", description: "Debug UsersListProvider" },
      ]
    },
    {
      group: "Services",
      options: [
        { value: "assetsService", label: "Assets Service", description: "Debug Assets Service" },
        { value: "assuranceActionsService", label: "Assurance Actions Service", description: "Debug Assurance Actions Service" },
        { value: "authService", label: "Auth Service", description: "Debug Auth Service" },
        { value: "companyService", label: "Company Service", description: "Debug Company Service" },
        { value: "complianceService", label: "Compliance Service", description: "Debug Compliance Service" },
        { value: "controlsService", label: "Controls Service", description: "Debug Controls Service" },
        { value: "dashboardDataService", label: "Dashboard Data Service", description: "Debug Dashboard Data Service" },
        { value: "dataRetentionService", label: "Data Retention Service", description: "Debug Data Retention Service" },
        { value: "documentationService", label: "Documentation Service", description: "Debug Documentation Service" },
        { value: "documentsService", label: "Documents Service", description: "Debug Documents Service" },
        { value: "ismsRolesService", label: "ISMS Roles Service", description: "Debug ISMS Roles Service" },
        { value: "legalObligationsService", label: "Legal Obligations Service", description: "Debug Legal Obligations Service" },
        { value: "logoutService", label: "Logout Service", description: "Debug Logout Service" },
        { value: "menusService", label: "Menus Service", description: "Debug Menus Service" },
        { value: "frameworkService", label: "Framework Service", description: "Debug Framework Service" },
        { value: "objectivesService", label: "Objectives Service", description: "Debug Objectives Service" },
        { value: "policyService", label: "Policy Service", description: "Debug Policy Service" },
        { value: "privacyRequestTracker", label: "Privacy Request Tracker", description: "Debug Privacy Request Tracker" },
        { value: "readingProgressService", label: "Reading Progress Service", description: "Debug Reading Progress Service" },
        { value: "risksService", label: "Risks Service", description: "Debug Risks Service" },
        { value: "rolesService", label: "Roles Service", description: "Debug Roles Service" },
        { value: "scopeService", label: "Scope Service", description: "Debug Scope Service" },
        { value: "soaService", label: "SOA Service", description: "Debug SOA Service" },
        { value: "soaUpdatesService", label: "SOA Updates Service", description: "Debug SOA Updates Service" },
        { value: "specialInterestGroupsService", label: "Special Interest Groups Service", description: "Debug Special Interest Groups Service" },
        { value: "userReportsService", label: "User Reports Service", description: "Debug User Reports Service" },
        { value: "userService", label: "User Service", description: "Debug User Service" },
        { value: "vendorScoreService", label: "Vendor Score Service", description: "Debug Vendor Score Service" },
      ]
    }
  ]

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6">
        {/* <h2 className="text-xl font-semibold mb-4 text-foreground">Security Settings</h2> */}
        <BeautifulTabsSelector
          options={TAB_GROUPS}
          value={selectedTab}
          onChange={setSelectedTab}
          selectorLabel="Diagnostics"
        />
        <p className="text-sm text-muted-foreground mb-6">
          View diagnostics information to help you debug and resolve issues.
        </p>

        <Tabs defaultValue={TAB_GROUPS[0].options[0]?.value} value={selectedTab} className="w-full">
          <TabsContent value={selectedTab}>
            <div className="space-y-4">
              {debugExports && (
                <div className="bg-muted/30 p-2 rounded text-xs mb-2">
                  <div><b>Debug: Exported keys/types</b></div>
                  <div>Named: {JSON.stringify(debugExports.named)}</div>
                  {debugExports.default && <div>Default: {JSON.stringify(debugExports.default)}</div>}
                  {debugExports.error && <div className="text-destructive">Import error: {debugExports.error}</div>}
                </div>
              )}
              {exportedList.length === 0 && (
                <div className="text-muted-foreground text-sm">No exported values found for this module.</div>
              )}
              {exportedList.map(({ name, value, type, from }) => (
                <div key={from + ':' + name} className="border rounded-lg p-4 mb-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="font-mono text-sm">{name}</span>
                      <span className="ml-2 text-xs text-muted-foreground">[{type}{from === 'default' ? ', default' : ''}]</span>
                    </div>
                    {type === 'function' && (
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={results[name]?.loading}
                        onClick={() => handleRunFunction(name, value)}
                      >
                        {results[name]?.loading ? 'Running...' : 'Run'}
                      </Button>
                    )}
                    {(type === 'object' || type === 'string' || type === 'number' || type === 'boolean') && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setExpanded(e => ({ ...e, [from + ':' + name]: !e[from + ':' + name] }))}
                      >
                        {expanded[from + ':' + name] ? 'Hide' : 'Show'}
                      </Button>
                    )}
                  </div>
                  {/* Value inspector */}
                  {type === 'function' && results[name]?.result !== undefined && !name.startsWith('use') && (
                    <pre className="bg-muted mt-2 p-2 rounded text-xs overflow-x-auto">
                      {JSON.stringify(results[name].result, null, 2)}
                    </pre>
                  )}
                  {type === 'function' && results[name]?.error && !name.startsWith('use') && (
                    <pre className="bg-destructive/10 mt-2 p-2 rounded text-xs text-destructive overflow-x-auto">
                      {String(results[name].error)}
                    </pre>
                  )}
                  {expanded[from + ':' + name] && type !== 'function' && (
                    <pre className="bg-muted mt-2 p-2 rounded text-xs overflow-x-auto">
                      {JSON.stringify(value, null, 2)}
                    </pre>
                  )}
                </div>
              ))}

              {/* Hook runner dialog */}
              <Dialog open={hookDialog.open} onOpenChange={open => setHookDialog(h => ({ ...h, open }))}>
                <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto bg-card p-0">
                  <div className="p-6">
                    <DialogHeader>
                      <DialogTitle>Run Hook: {hookDialog.name}</DialogTitle>
                    </DialogHeader>
                    {hookDialog.hookFn && (
                      <div className="py-4">
                        <HookRunner hookFn={hookDialog.hookFn} hookName={hookDialog.name} />
                      </div>
                    )}
                    <DialogFooter className="pt-4">
                      <Button onClick={() => setHookDialog({ open: false, name: '', hookFn: null })}
                        className="mt-4">
                        Close
                      </Button>
                    </DialogFooter>
                  </div>
                </DialogContent>
              </Dialog>

              {/* Fn runner dialog for normal functions */}
              <Dialog open={fnDialog.open} onOpenChange={open => setFnDialog(h => ({ ...h, open }))}>
                <DialogContent className="w-[95vw] max-w-[95vw] max-h-[90vh] overflow-y-auto bg-card p-0">
                  <div className="p-6">
                    <DialogHeader>
                      <DialogTitle>Run Function: {fnDialog.name}</DialogTitle>
                    </DialogHeader>
                    {fnDialog.fn && (
                      <div className="py-4">
                        <FnRunner 
                          fn={fnDialog.fn} 
                          fnName={fnDialog.name} 
                          onClose={() => setFnDialog({ open: false, name: '', fn: null })} 
                        />
                      </div>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
